import { useEffect, useState } from "react";
import { UserPlus, Pencil, Trash2, ShieldCheck, KeyRound, Copy } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { email: "", name: "", password: "", role: "user", permissions: ["view"], is_active: true, properties: null, department: "" };

const roleBadge = (role) => role === "super_admin" ? <Badge className="bg-emerald-600 text-white border-0"><ShieldCheck size={12} className="mr-1" /> Super admin</Badge>
  : role === "general_manager" ? <Badge className="bg-sky-700 text-white border-0">General Manager</Badge>
  : role === "department" ? <Badge className="bg-amber-100 text-amber-900 border-0">Department</Badge>
  : <Badge className="bg-slate-100 text-slate-700 border-0">User</Badge>;

const UserDialog = ({ open, onOpenChange, perms, editing, onSaved, properties = [], departments = [] }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(editing ? { ...editing, password: "", properties: editing.properties ?? null, department: editing.department || "" } : EMPTY); }, [editing, open]);
  const toggle = (id) => setForm((f) => ({ ...f, permissions: f.permissions.includes(id) ? f.permissions.filter((p) => p !== id) : [...f.permissions, id] }));
  const allProps = form.properties === null;
  const toggleProp = (name) => setForm((f) => { const cur = f.properties || []; return { ...f, properties: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] }; });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const dept = form.role === "department" ? form.department || null : null;
      if (editing) {
        const body = { name: form.name, role: form.role, permissions: form.permissions, is_active: form.is_active, all_properties: allProps, properties: allProps ? null : form.properties, department: dept };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", { email: form.email, name: form.name, password: form.password, role: form.role, permissions: form.permissions, properties: allProps ? null : form.properties, department: dept });
        toast.success("User created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const isSuper = form.role === "super_admin";
  const bound = ["general_manager", "department"].includes(form.role);
  const ROLES = [["user", "User"], ["general_manager", "General Manager"], ["department", "Department head"], ["super_admin", "Super admin"]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? "Edit access" : "Create user"}</DialogTitle>
          <DialogDescription>Only registered Armella Group users can sign in. Tick the access each user should have.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label>
            <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} minLength={6} data-testid="user-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <div className="inline-flex flex-wrap rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid="user-role-toggle">
              {ROLES.map(([id, lbl]) => (
                <button key={id} type="button" onClick={() => setForm({ ...form, role: id, properties: ["general_manager", "department"].includes(id) && form.properties === null ? [] : form.properties, permissions: id === "general_manager" && !form.permissions.includes("edit") ? [...form.permissions, "edit"] : form.permissions })} data-testid={`user-role-${id.replace("_", "-")}`}
                  className={`px-3 py-1.5 rounded ${form.role === id ? (id === "super_admin" ? "bg-emerald-700 text-white" : "bg-slate-900 text-white") : "text-slate-600"}`}>{lbl}</button>
              ))}
            </div>
            {form.role === "general_manager" && <p className="text-[11px] text-slate-500">Sees the whole hotel budget, can override every department and creates the department heads of the hotels selected below (Team page).</p>}
            {form.role === "department" && <p className="text-[11px] text-slate-500">Works only on the sheets of his/her department (HR also sees all payroll). The General Manager is notified when the department marks its budget complete.</p>}
          </div>
          {form.role === "department" && (
            <div className="space-y-1.5">
              <Label>Department</Label>
              <select value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} required className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="user-department-select">
                <option value="" disabled>Select department…</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          {!isSuper && (
            <div className="space-y-2" data-testid="permission-checkboxes">
              <Label>Access</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                {perms.map((p) => (
                  <label key={p.id} className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer text-sm ${form.permissions.includes(p.id) ? "border-emerald-400 bg-emerald-50" : "border-slate-200"}`}>
                    <input type="checkbox" checked={form.permissions.includes(p.id)} disabled={p.id === "view"} onChange={() => toggle(p.id)} data-testid={`permission-${p.id}`} className="mt-0.5 accent-emerald-600" />
                    <span><span className="font-medium text-slate-900">{p.label}</span><br /><span className="text-[11px] text-slate-500">{p.hint}</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {!isSuper && (
            <div className="space-y-2" data-testid="property-access">
              <Label>{bound ? "Hotel(s) this user is bound to" : "Property access"}</Label>
              <div className="flex flex-wrap gap-2">
                {!bound && <button type="button" onClick={() => setForm({ ...form, properties: null })} data-testid="property-access-all" className={`rounded-full px-3 py-1 text-xs border ${allProps ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600"}`}>All properties</button>}
                {properties.map((p) => (
                  <button key={p.id} type="button" onClick={() => { if (allProps) setForm({ ...form, properties: [p.name] }); else toggleProp(p.name); }} data-testid={`property-access-${p.id}`}
                    className={`rounded-full px-3 py-1 text-xs border ${!allProps && form.properties?.includes(p.name) ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-600"}`}>{p.name}</button>
                ))}
              </div>
              {!allProps && !(form.properties || []).length && <p className="text-[11px] text-amber-700" data-testid="property-access-warning">{bound ? "Select at least one hotel — a General Manager / department head must be bound to a property." : "No property selected — this user will not see any project."}</p>}
            </div>
          )}
          {editing && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="user-active-checkbox" className="accent-emerald-600" /> Account active</label>
          )}
          <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-emerald-700" data-testid="user-save-button">{busy ? "Saving..." : editing ? "Save changes" : "Create user"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TempPasswordDialog = ({ temp, onClose }) => (
  <Dialog open={!!temp} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="temp-password-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading inline-flex items-center gap-2"><KeyRound size={18} className="text-emerald-600" /> Temporary password issued</DialogTitle>
        <DialogDescription>Share this password with <span className="font-medium text-slate-900">{temp?.email}</span> through a secure channel. They must set a new password at their next sign-in. It will not be shown again.</DialogDescription>
      </DialogHeader>
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <code className="flex-1 font-mono text-lg text-slate-900 select-all" data-testid="temp-password-value">{temp?.temp_password}</code>
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(temp.temp_password); toast.success("Copied"); }} data-testid="temp-password-copy"><Copy size={14} className="mr-1" /> Copy</Button>
      </div>
      <Button onClick={onClose} className="w-full bg-slate-900 hover:bg-emerald-700" data-testid="temp-password-close">Done</Button>
    </DialogContent>
  </Dialog>
);

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState([]);
  const [props, setProps] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errorText(e)));
  const [temp, setTemp] = useState(null);
  const resetPassword = async (u) => {
    if (!window.confirm(`Issue a temporary password for ${u.name}? Their current password will stop working.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTemp(data);
      load();
    } catch (e) { toast.error(errorText(e)); }
  };
  useEffect(() => { load(); api.get("/admin/permissions").then((r) => setPerms(r.data)).catch(() => {}); api.get("/properties").then((r) => setProps(r.data.filter((p) => p.is_active))).catch(() => {}); api.get("/team/departments").then((r) => setDepartments(r.data)).catch(() => {}); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const label = (id) => perms.find((p) => p.id === id)?.label || id;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AppNav />
      <main className="px-4 sm:px-8 lg:px-12 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 rise">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-emerald-700 font-semibold">Super admin</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-2">Users & Access</h1>
            <p className="text-slate-600 mt-3 max-w-2xl text-sm sm:text-base">Public registration is closed. Create accounts for Armella Group staff and tick exactly what each person may do.</p>
          </div>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="h-11 bg-slate-900 hover:bg-emerald-700 px-5" data-testid="create-user-button"><UserPlus size={16} className="mr-2" /> Create user</Button>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-auto" data-testid="users-table">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-white text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Access</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className="px-4 py-3"><p className="font-medium text-slate-900">{u.name}{u.id === me?.id ? <span className="text-xs text-slate-400"> (you)</span> : null}</p><p className="text-xs text-slate-500 font-mono">{u.email}</p></td>
                  <td className="px-4 py-3">{roleBadge(u.role)}{u.role === "department" && u.department && <p className="text-[11px] text-slate-600 mt-1" data-testid={`user-department-${u.id}`}>{u.department}</p>}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">{u.role === "super_admin" ? <span className="text-xs text-slate-500">All access</span> : u.permissions.map((p) => <span key={p} className="text-[11px] bg-slate-100 text-slate-700 rounded px-2 py-0.5">{label(p)}</span>)}</div>
                    {u.role !== "super_admin" && <p className="text-[11px] text-slate-500 mt-1" data-testid={`user-properties-${u.id}`}>{u.properties === null || u.properties === undefined ? "All properties" : u.properties.length ? `Properties: ${u.properties.join(", ")}` : "No property access"}</p>}
                  </td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{u.is_active ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Active</Badge> : <Badge className="bg-red-100 text-red-700 border-0">Deactivated</Badge>}
                    {u.reset_requested_at && <Badge className="bg-amber-100 text-amber-800 border-0" data-testid={`reset-requested-${u.id}`}><KeyRound size={11} className="mr-1" /> Reset requested {new Date(u.reset_requested_at).toLocaleDateString("en-GB")}</Badge>}
                    {u.must_change_password && <Badge className="bg-slate-100 text-slate-700 border-0" data-testid={`temp-password-${u.id}`}>Temporary password</Badge>}</div></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(u); setOpen(true); }} className="text-slate-400 hover:text-emerald-700 p-1" title="Edit access / reset password" data-testid={`edit-user-${u.id}`}><Pencil size={15} /></button>
                    <button onClick={() => resetPassword(u)} className={`p-1 ${u.reset_requested_at ? "text-amber-600 hover:text-amber-800" : "text-slate-400 hover:text-emerald-700"}`} title="Reset password (issue temporary password)" data-testid={`reset-password-${u.id}`}><KeyRound size={15} /></button>
                    {u.id !== me?.id && <button onClick={() => remove(u)} className="text-slate-400 hover:text-red-600 p-1" title="Delete" data-testid={`delete-user-${u.id}`}><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      <TempPasswordDialog temp={temp} onClose={() => setTemp(null)} />
      </main>
      <UserDialog open={open} onOpenChange={setOpen} perms={perms} editing={editing} onSaved={load} properties={props} departments={departments} />
    </div>
  );
}
